export default function Privacy() {
    return (
        <div className="max-w-[900px] mx-auto px-6 md:px-10 py-16" data-testid="privacy-page">
            <div className="overline mb-3">Legal</div>
            <h1 className="font-display text-4xl md:text-5xl leading-tight">Privacy Notice</h1>
            <div className="text-xs uppercase tracking-[0.12em] text-[#9a9a9a] mt-4">
                Last updated · {new Date().toLocaleDateString(undefined, { year: "numeric", month: "long" })}
            </div>

            <div className="report-prose mt-10 space-y-4">
                <p>
                    SmartNote2Self License Risk Scan ("the platform", "we", "our") is an independent
                    Microsoft licensing advisory tool. We are not Microsoft, not a reseller, and we
                    do not recommend specific vendors. We respect your privacy and are committed to
                    GDPR-level data-handling standards, regardless of where you operate.
                </p>

                <h2>1. What we collect</h2>
                <ul>
                    <li>
                        <strong>Account data</strong>: the name, email and (optionally) organisation
                        you supply at sign-up. If you sign in with Google, we receive your Google
                        profile email and display name.
                    </li>
                    <li>
                        <strong>Assessment data</strong>: the organisation context (industry, user
                        count, current Microsoft agreement, license types) and the responses you
                        choose for the assessment questionnaire.
                    </li>
                    <li>
                        <strong>Generated reports</strong>: the markdown report produced by our AI
                        engine from your inputs, stored against your account.
                    </li>
                    <li>
                        <strong>Operational logs</strong>: a minimal record of API requests for
                        security and reliability — not used for marketing or profiling.
                    </li>
                </ul>

                <h2>2. What we do NOT do</h2>
                <ul>
                    <li>We do not sell your data.</li>
                    <li>
                        We do not share your responses with Microsoft, with any reseller, or with
                        any other licensing partner.
                    </li>
                    <li>
                        We do not use your assessment data to train external AI models. Your inputs
                        are sent to our LLM provider (Anthropic, via the Emergent platform) for the
                        sole purpose of producing your report, and are not used by the provider for
                        training under our enterprise terms.
                    </li>
                </ul>

                <h2>3. Your rights (GDPR-aligned)</h2>
                <ul>
                    <li>
                        <strong>Right to access</strong>: every assessment you have submitted is
                        visible to you on your dashboard at <code>/dashboard</code>.
                    </li>
                    <li>
                        <strong>Right to rectification</strong>: you may retake an assessment at any
                        time. For account details, contact us at the address below.
                    </li>
                    <li>
                        <strong>Right to erasure</strong>: from your dashboard, the red{" "}
                        <em>"Delete my account"</em> button permanently deletes your user record and
                        every assessment you have submitted. A single click. No questions asked.
                    </li>
                    <li>
                        <strong>Right to portability</strong>: download any report as a PDF from the
                        report page. Contact us if you require a structured export.
                    </li>
                    <li>
                        <strong>Right to object / restrict processing</strong>: write to us at the
                        contact below.
                    </li>
                </ul>

                <h2>4. Uploaded supporting documents</h2>
                <p>
                    When you (or your consultant) upload supporting documents to an
                    assessment — for example a Microsoft 365 license export, an
                    Enterprise Agreement summary, an invoice, a usage report or a
                    spreadsheet of users — they are stored against the assessment in
                    our database and used only to inform the AI report that you
                    request. We extract the text content for the AI to read and store
                    that alongside the original file metadata.
                </p>
                <ul>
                    <li>
                        Uploaded documents are <strong>never shared</strong> with
                        Microsoft, with any reseller, or with any other licensing
                        partner.
                    </li>
                    <li>
                        Deleting an assessment from your dashboard immediately deletes
                        every supporting document attached to it.
                    </li>
                    <li>
                        Deleting your account deletes every assessment and every
                        attached document.
                    </li>
                    <li>
                        We recommend uploading the <em>minimum</em> evidence needed —
                        for example, a license summary with user counts is enough; a
                        full user-level export with personal data is not required.
                    </li>
                </ul>

                <h2>5. Retention</h2>
                <p>
                    We retain your assessments only for as long as you keep your account. If you
                    delete your account, every assessment you submitted is deleted alongside it.
                </p>

                <h2>5. Security</h2>
                <p>
                    Passwords are stored using bcrypt one-way hashes. Authentication uses short-lived
                    JSON Web Tokens. Connections are encrypted in transit. The platform admin can
                    view assessments for support purposes only.
                </p>

                <h2>7. Cookies</h2>
                <p>
                    We use one essential authentication cookie to keep you signed in. We do not use
                    advertising or tracking cookies.
                </p>

                <h2>8. Sub-processors</h2>
                <ul>
                    <li>
                        <strong>Anthropic Claude (via Emergent platform)</strong> — used to generate
                        the executive report from your inputs.
                    </li>
                    <li>
                        <strong>Google</strong> — only if you choose Google sign-in; we receive your
                        profile email and display name.
                    </li>
                </ul>

                <h2>9. Contact</h2>
                <p>
                    For any data-protection request — including erasure, access or portability —
                    contact{" "}
                    <a href="mailto:smartmarcelle@gmail.com" className="text-[#00A896]">
                        smartmarcelle@gmail.com
                    </a>
                    .
                </p>

                <h2>10. Disclaimer</h2>
                <p>
                    Reports produced by the platform are advisory only. They do not constitute legal
                    advice, licensing certification, compliance certification, contractual
                    interpretation, or procurement approval. All Microsoft licensing decisions
                    should be validated against current Microsoft Product Terms, applicable
                    agreements, and your own procurement requirements.
                </p>
            </div>
        </div>
    );
}
